import React from 'react';
import { Alert, Button } from '@amzn/awsui-components-react-v3/polaris';

/**
 * Alerts for load and save errors. Messages and RetryAlert are used by the base rates and pricing
 * model pages; SaveAlerts and RowErrorSummary by the base rates table and upload.
 */

/** One line per message. */
export const Messages = ({ messages }: { messages: string[] }): JSX.Element => (
  <>
    {messages.map((message, index) => (
      <div key={index}>{message}</div>
    ))}
  </>
);

/** A failed load, with a button to try again. */
export const RetryAlert = ({
  id,
  header,
  messages,
  retryId,
  onRetry,
}: {
  id: string;
  header: string;
  messages: string[];
  retryId: string;
  onRetry: () => void;
}): JSX.Element => (
  <Alert
    id={id}
    type="error"
    header={header}
    action={
      <Button id={retryId} onClick={onRetry}>
        Retry
      </Button>
    }
  >
    <Messages messages={messages} />
  </Alert>
);

/**
 * The outcome of a failed save: a version conflict, with a reload action, and form-level errors.
 * Element ids start with `idPrefix`.
 */
export const SaveAlerts = ({
  idPrefix,
  conflictHeader,
  conflict,
  formErrors,
  onReload,
}: {
  idPrefix: string;
  conflictHeader: string;
  conflict: string[] | null;
  formErrors: string[];
  onReload: () => void;
}): JSX.Element => (
  <>
    {conflict && (
      <Alert
        id={`${idPrefix}Conflict`}
        type="warning"
        header={conflictHeader}
        action={
          <Button id={`${idPrefix}Reload`} onClick={onReload}>
            Reload
          </Button>
        }
      >
        <Messages messages={conflict} />
      </Alert>
    )}
    {formErrors.length > 0 && (
      <Alert id={`${idPrefix}FormErrors`} type="error">
        <Messages messages={formErrors} />
      </Alert>
    )}
  </>
);

/** Lists the rows that have errors so ones on other pages are not missed. */
export const RowErrorSummary = ({
  idPrefix,
  rowNumbers,
  onShowFirst,
}: {
  idPrefix: string;
  rowNumbers: number[];
  onShowFirst: () => void;
}): JSX.Element | null =>
  rowNumbers.length === 0 ? null : (
    <Alert
      id={`${idPrefix}RowErrors`}
      type="error"
      header={`Fix the errors in ${rowNumbers.length === 1 ? 'row' : 'rows'} ${rowNumbers.join(
        ', '
      )}`}
      action={
        <Button id={`${idPrefix}ShowFirstRowError`} onClick={onShowFirst}>
          Show row {rowNumbers[0]}
        </Button>
      }
    />
  );
